import { ElementType, forwardRef, memo, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import ArrowIcon from 'Assets/reactSvgs/ArrowIcon';
import CloseXIcon from 'Assets/reactSvgs/CloseXIcon';
import { commonButtonStyle } from 'Styles/sharedStyle';

/** For a close button (with an "x"). */
export const NAVIGATION_BUTTON_PURPOSE_CLOSE = 'NAVIGATION_BUTTON_PURPOSE_CLOSE';

/** For a back button (with a back arrow). */
export const NAVIGATION_BUTTON_PURPOSE_BACK = 'NAVIGATION_BUTTON_PURPOSE_BACK';

export type NavigationButtonPurpose =
  | typeof NAVIGATION_BUTTON_PURPOSE_CLOSE
  | typeof NAVIGATION_BUTTON_PURPOSE_BACK;

export type CloseXTheme = 'custom' | 'dark' | 'lite' | 'white';

type Props = {
  theme?: CloseXTheme;
  withBorder?: boolean;
  hiddenText?: string;
  iconClassName?: string;
  iconSize?: string;
  paddingClassName?: string;
  purpose?: NavigationButtonPurpose;
  shouldFocusCloseButton?: boolean;
} & React.HTMLAttributes<HTMLButtonElement>;

/**
 * Renders a close button (with an "x") or back button (with a back arrow).
 *
 * TODO: Rename to NavigationButton after TS migration is complete.
 */
const CloseX = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    className,
    hiddenText,
    iconClassName: iconClassNameProp,
    iconSize = '28px',
    onClick,
    paddingClassName,
    theme = 'lite',
    withBorder = true,
    purpose = NAVIGATION_BUTTON_PURPOSE_CLOSE,
    shouldFocusCloseButton = false,
    ...restProps
  } = props;
  const HTMLElementComponent = (onClick ? 'button' : 'div') as ElementType;
  const { formatMessage } = useIntl();
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const buttonRef = (ref as React.RefObject<HTMLButtonElement>) || closeBtnRef;

  const closeAccessibilityText = formatMessage({
    id: purpose === NAVIGATION_BUTTON_PURPOSE_CLOSE ? 'close' : 'back',
  });
  const iconClassName = classNames(`Sq(${iconSize}) P(4px)`, iconClassNameProp);

  useEffect(() => {
    if (buttonRef.current && shouldFocusCloseButton) {
      buttonRef.current.focus();
    }
  }, [buttonRef, shouldFocusCloseButton]);

  return (
    <HTMLElementComponent
      {...restProps}
      className={classNames(
        {
          'C($c-ds-icon-secondary) Bdc($c-ds-icon-secondary) Bdc($c-ds-icon-secondary-inverse):h C($c-ds-icon-secondary-inverse):h':
            theme === 'lite',
          'C($c-ds-icon-secondary-inverse) Bdc($c-ds-icon-secondary-inverse) Bdc($c-ds-icon-secondary):h C($c-ds-icon-secondary):h':
            theme === 'dark',
          'C($c-ds-icon-primary-overlay) Bdc($c-ds-icon-primary-overlay) Bdc($c-ds-icon-primary-inverse):h C($c-ds-icon-primary-inverse):h':
            theme === 'white',
          'Bdrs(50%) Bds(s) Bdw(3px)': withBorder,
          'Trsdu($fast) Trsp($transform) Rotate(-90deg):h--ml': withBorder,
          // NOTE: "close" className doesn't seem to serve any stylistic purpose but is kept here in case it is needed for testing purposes.
          close: purpose === NAVIGATION_BUTTON_PURPOSE_CLOSE,
        },
        'Cur(p)',
        commonButtonStyle,
        className,
        paddingClassName
      )}
      onClick={onClick}
      type={onClick ? 'button' : undefined}
      data-testid={purpose === NAVIGATION_BUTTON_PURPOSE_CLOSE ? 'close' : undefined}
      ref={buttonRef}
    >
      {purpose === NAVIGATION_BUTTON_PURPOSE_CLOSE ? (
        <CloseXIcon className={iconClassName} />
      ) : (
        <ArrowIcon className={iconClassName} />
      )}
      <span className="Hidden">{hiddenText ?? closeAccessibilityText}</span>
    </HTMLElementComponent>
  );
});

export default memo(CloseX);
